import type { Key } from "react";
import { useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, ChevronSelectorVertical } from "@untitledui/icons";
import { Select, Table, Tooltip, TooltipTrigger, cx } from "@tricura/atlas-ui";
import { ARCHIVED_ROW, ARCHIVED_TOOLTIP_LEGAL } from "../../../components/facility-archive-row";
import { FacilityRowActionsMenu } from "../../../components/facility-row-actions-menu";
import type { LitigationFacilityRow, MarkerRiskLevel } from "../../../types/litigation";

const RISK_LABEL: Record<MarkerRiskLevel, string> = {
    low: "Low Risk",
    "almost-high": "Almost High",
    medium: "Medium Risk",
    high: "High Risk",
    "weak-match": "Weak Match",
};

/** Pastel-pill styles for the Risk column. Each entry is the
 *  background / border / text triplet per risk tier; colours come from
 *  atlas-ui utility tokens so dark mode flips them automatically.
 *
 *  Spec source: product Figma (May 2026 refresh) — 1px border,
 *  fully-rounded capsule, no shadow. Almost-High shares the High
 *  treatment until the design team specs a distinct tier. */
const RISK_PILL_CLASSES: Record<MarkerRiskLevel, string> = {
    low: "bg-utility-green-50 border-utility-green-200 text-utility-green-700",
    "almost-high": "bg-utility-orange-50 border-utility-orange-200 text-utility-orange-700",
    medium: "bg-utility-yellow-50 border-utility-yellow-200 text-utility-yellow-700",
    high: "bg-utility-orange-50 border-utility-orange-200 text-utility-orange-700",
    "weak-match": "bg-utility-neutral-50 border-primary text-utility-neutral-700",
};

function RiskPill({ level }: { level: MarkerRiskLevel }) {
    return (
        <span
            data-autotest="legal-heatmap-table-risk-badge"
            className={cx(
                "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium whitespace-nowrap",
                RISK_PILL_CLASSES[level],
            )}
        >
            {RISK_LABEL[level]}
        </span>
    );
}

/** Severity ordinal used when sorting by the Risk column. weak-match
 *  is the "no signal" tier so it sits at the bottom on ascending sort,
 *  with severity climbing toward `high`. */
const RISK_ORDER: Record<MarkerRiskLevel, number> = {
    "weak-match": 0,
    low: 1,
    medium: 2,
    "almost-high": 3,
    high: 4,
};

/** Pull the leading number out of values like "12/100" or "3" so the
 *  Claims column sorts numerically rather than lexicographically. */
function parseClaims(raw: string): number {
    const n = parseFloat(String(raw).split("/")[0]);
    return Number.isFinite(n) ? n : 0;
}

type SortField = "_index" | "facilityName" | "type" | "county" | "state" | "lawsuits" | "riskLevel";
type SortDir = "asc" | "desc";

/** Per-cell help text for the columns that need an explanation. Used
 *  by atlas-ui's `Table.Head` `tooltip` prop — it renders a HelpCircle
 *  icon next to the column title and shows this string on hover. */
const CLAIMS_TOOLTIP =
    "Lawsuits filed against the facility expressed as a ratio per 100 licensed beds. A higher number means greater litigation exposure relative to the facility's size.";
const RISK_TOOLTIP =
    "Composite risk level derived from claims activity, severity, and exposure. The exact scoring model is being finalized — values shown here are preliminary.";

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50] as const;
const DEFAULT_PAGE_SIZE = 5;

interface HeatmapTableProps {
    rows: LitigationFacilityRow[];
    /** Forwarded to the underlying atlas-ui `<Table>` chrome — useful
     *  when the parent card already supplies its own border / rounding
     *  and the default Table chrome would double up. */
    className?: string;
    /** Forwarded to the Pagination footer — same purpose as `className`,
     *  just for the bottom strip's own rounded corners / borders. */
    paginationClassName?: string;
    /** Archived facilities (OR-1431) stay listed here as historical data but
     *  render struck through — they're hidden from the map above, so the row
     *  carries a tooltip saying so. */
    isRowArchived?: (row: LitigationFacilityRow) => boolean;
    /**
     * Restores an archived row's facility (by `locationId`) — a GLOBAL action
     * cascading to Schedule/Regulatory/Operational/Loss Runs. Legal never
     * offers Archive (that stays a Schedule-initiated action); the ⋮ here
     * only ever shows Restore, and only on rows whose facility is archived —
     * active rows get no menu at all, since there's nothing to do.
     */
    onRestoreFacility?: (locationId: string) => void;
}

export function HeatmapTable({ rows, className, paginationClassName, isRowArchived, onRestoreFacility }: HeatmapTableProps) {
    const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
    const [page, setPage] = useState(1);
    const [sort, setSort] = useState<{ field: SortField; dir: SortDir } | null>(null);

    // Reset to page 1 whenever the underlying dataset shape changes
    // (filters applied upstream). A pure sort change doesn't trip this
    // — the page slice tracks the same row count, just in new order.
    useEffect(() => {
        setPage(1);
    }, [rows.length]);

    /** Decorate each row with a 1-based index that survives sorting.
     *  This drives the `#` column AND lets the user resort back to
     *  the original feed order at any time. */
    const indexedRows = useMemo(() => rows.map((r, i) => ({ ...r, _index: i + 1 })), [rows]);

    /** Local sort — runs in-memory over the rows the parent already
     *  filtered for us. Three-state cycle on each header: unsorted →
     *  asc → desc → unsorted. */
    const sortedRows = useMemo(() => {
        if (!sort) return indexedRows;
        const { field, dir } = sort;
        const arr = [...indexedRows];
        arr.sort((a, b) => {
            let cmp: number;
            if (field === "_index") {
                cmp = a._index - b._index;
            } else if (field === "lawsuits") {
                cmp = parseClaims(a.lawsuitsPer100Beds) - parseClaims(b.lawsuitsPer100Beds);
            } else if (field === "riskLevel") {
                cmp = RISK_ORDER[a.riskLevel] - RISK_ORDER[b.riskLevel];
            } else {
                const av = String(a[field] ?? "").toLowerCase();
                const bv = String(b[field] ?? "").toLowerCase();
                cmp = av < bv ? -1 : av > bv ? 1 : 0;
            }
            return dir === "asc" ? cmp : -cmp;
        });
        return arr;
    }, [indexedRows, sort]);

    const handleSort = (field: SortField) => {
        setSort((current) => {
            if (!current || current.field !== field) return { field, dir: "asc" };
            if (current.dir === "asc") return { field, dir: "desc" };
            return null;
        });
    };

    const totalRows = sortedRows.length;
    const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
    const safePage = Math.min(Math.max(1, page), totalPages);
    const startIdx = (safePage - 1) * pageSize;
    const endIdx = Math.min(startIdx + pageSize, totalRows);

    const pageRows = useMemo(() => sortedRows.slice(startIdx, endIdx), [sortedRows, startIdx, endIdx]);

    return (
        <>
            <Table aria-label="Litigation facilities" className={className}>
                <Table.Header>
                    <Table.Head id="_index">
                        <SortHeader field="_index" sort={sort} onSortChange={handleSort}>
                            #
                        </SortHeader>
                    </Table.Head>
                    <Table.Head id="facilityName" isRowHeader>
                        <SortHeader field="facilityName" sort={sort} onSortChange={handleSort}>
                            Facility Name
                        </SortHeader>
                    </Table.Head>
                    <Table.Head id="type">
                        <SortHeader field="type" sort={sort} onSortChange={handleSort}>
                            Type
                        </SortHeader>
                    </Table.Head>
                    <Table.Head id="county">
                        <SortHeader field="county" sort={sort} onSortChange={handleSort}>
                            County
                        </SortHeader>
                    </Table.Head>
                    <Table.Head id="state">
                        <SortHeader field="state" sort={sort} onSortChange={handleSort}>
                            State
                        </SortHeader>
                    </Table.Head>
                    <Table.Head id="lawsuits" tooltip={CLAIMS_TOOLTIP}>
                        <SortHeader field="lawsuits" sort={sort} onSortChange={handleSort}>
                            Lawsuits
                        </SortHeader>
                    </Table.Head>
                    <Table.Head id="riskLevel" tooltip={RISK_TOOLTIP}>
                        <SortHeader field="riskLevel" sort={sort} onSortChange={handleSort}>
                            Risk
                        </SortHeader>
                    </Table.Head>
                    <Table.Head id="actions" label="" />
                </Table.Header>
                <Table.Body items={pageRows.map((r, i) => ({ ...r, _id: `${r.facilityName}-${startIdx + i}` }))}>
                    {(row) => (
                        <Table.Row
                            id={row._id}
                            data-autotest="legal-heatmap-table-row"
                            className={isRowArchived?.(row) ? ARCHIVED_ROW : undefined}
                        >
                            <Table.Cell data-autotest="legal-heatmap-table-index-cell">{row._index}</Table.Cell>
                            <Table.Cell data-autotest="legal-heatmap-table-facility-name-cell">
                                {isRowArchived?.(row) ? (
                                    <Tooltip title={ARCHIVED_TOOLTIP_LEGAL} placement="top">
                                        <TooltipTrigger className="pointer-events-auto cursor-help text-left">
                                            {row.facilityName || "—"}
                                        </TooltipTrigger>
                                    </Tooltip>
                                ) : (
                                    row.facilityName || "—"
                                )}
                            </Table.Cell>
                            <Table.Cell data-autotest="legal-heatmap-table-type-cell">{row.type || "—"}</Table.Cell>
                            <Table.Cell data-autotest="legal-heatmap-table-county-cell">{row.county || "—"}</Table.Cell>
                            <Table.Cell data-autotest="legal-heatmap-table-state-cell">{row.state || "—"}</Table.Cell>
                            <Table.Cell data-autotest="legal-heatmap-table-lawsuits-cell">{row.lawsuitsPer100Beds || "—"}</Table.Cell>
                            <Table.Cell data-autotest="legal-heatmap-table-risk-cell">
                                <RiskPill level={row.riskLevel} />
                            </Table.Cell>
                            <Table.Cell>
                                {isRowArchived?.(row) && onRestoreFacility && (
                                    <div className="flex justify-end">
                                        <FacilityRowActionsMenu
                                            facilityName={row.facilityName}
                                            isArchived
                                            onRestore={() => onRestoreFacility(row.locationId)}
                                        />
                                    </div>
                                )}
                            </Table.Cell>
                        </Table.Row>
                    )}
                </Table.Body>
            </Table>

            {totalRows > DEFAULT_PAGE_SIZE && (
                <Pagination
                    currentPage={safePage}
                    totalPages={totalPages}
                    totalRows={totalRows}
                    startIdx={startIdx}
                    endIdx={endIdx}
                    pageSize={pageSize}
                    onPageChange={setPage}
                    onPageSizeChange={(s) => {
                        setPageSize(s);
                        setPage(1);
                    }}
                    className={paginationClassName}
                />
            )}
        </>
    );
}

// ─── Sortable header label ───────────────────────────────────────────────────

/** kebab-case slug for the sort button's data-autotest, e.g. "facilityName" → "facility-name". */
function fieldSlug(field: SortField): string {
    return field.replace(/^_/, "").replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
}

interface SortHeaderProps {
    field: SortField;
    sort: { field: SortField; dir: SortDir } | null;
    onSortChange: (field: SortField) => void;
    children: React.ReactNode;
}

function SortHeader({ field, sort, onSortChange, children }: SortHeaderProps) {
    const active = sort?.field === field;
    const dir = active ? sort!.dir : null;
    const Icon = active ? (dir === "asc" ? ArrowUp : ArrowDown) : ChevronSelectorVertical;
    const ariaSort = !active ? "none" : dir === "asc" ? "ascending" : "descending";
    return (
        <button
            type="button"
            onClick={() => onSortChange(field)}
            aria-sort={ariaSort}
            data-autotest={`legal-heatmap-table-${fieldSlug(field)}-sort-button`}
            // Typography mirrors atlas-ui's built-in Table.Head label
            // span (`text-xs font-semibold text-quaternary`) so the
            // text inside the sort button matches every other column
            // header on the page.
            className="text-quaternary inline-flex cursor-pointer items-center gap-1.5 text-xs font-semibold whitespace-nowrap outline-none focus-visible:rounded focus-visible:ring-2 focus-visible:ring-focus-ring"
        >
            {children}
            <Icon
                aria-hidden="true"
                className={cx("size-3.5 shrink-0", active ? "text-fg-secondary" : "text-fg-quaternary")}
            />
        </button>
    );
}

// ─── Pagination ──────────────────────────────────────────────────────────────

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    totalRows: number;
    startIdx: number;
    endIdx: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    className?: string;
}

function buildPageList(currentPage: number, totalPages: number): Array<number | "..."> {
    if (totalPages <= 7) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const out: Array<number | "..."> = [1];
    if (currentPage > 4) out.push("...");
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let i = start; i <= end; i++) out.push(i);
    if (currentPage < totalPages - 3) out.push("...");
    out.push(totalPages);
    return out;
}

function Pagination({ currentPage, totalPages, totalRows, startIdx, endIdx, pageSize, onPageChange, onPageSizeChange, className }: PaginationProps) {
    const pages = buildPageList(currentPage, totalPages);
    const sizeItems = PAGE_SIZE_OPTIONS.map((n) => ({ id: n, label: String(n) }));

    return (
        <div className={cx("border-secondary bg-primary flex flex-wrap items-center justify-between gap-3 border-t px-6 py-3", className)}>
            <span className="text-tertiary text-sm">
                Showing {startIdx + 1}-{endIdx} of {totalRows}
            </span>

            <nav aria-label="Pagination" className="flex items-center gap-1">
                <PageNavButton
                    label="Previous page"
                    icon={ChevronLeft}
                    disabled={currentPage <= 1}
                    onClick={() => onPageChange(currentPage - 1)}
                    data-autotest="legal-heatmap-table-pagination-prev-button"
                />
                {pages.map((p, i) =>
                    p === "..." ? (
                        <span key={`ellipsis-${i}`} className="text-tertiary px-2 text-sm" aria-hidden="true">
                            …
                        </span>
                    ) : (
                        <button
                            key={p}
                            type="button"
                            aria-current={p === currentPage ? "page" : undefined}
                            onClick={() => onPageChange(p)}
                            data-autotest={`legal-heatmap-table-pagination-page-button`}
                            className={cx(
                                "size-8 rounded-md text-sm transition duration-100 ease-linear",
                                p === currentPage
                                    ? "bg-secondary text-primary border-secondary border font-semibold"
                                    : "text-tertiary hover:bg-secondary_hover hover:text-secondary",
                            )}
                        >
                            {p}
                        </button>
                    ),
                )}
                <PageNavButton
                    label="Next page"
                    icon={ChevronRight}
                    disabled={currentPage >= totalPages}
                    onClick={() => onPageChange(currentPage + 1)}
                    data-autotest="legal-heatmap-table-pagination-next-button"
                />
            </nav>

            <div className="flex items-center gap-2">
                <span className="text-tertiary text-sm">Results per page</span>
                <Select
                    size="sm"
                    aria-label="Results per page"
                    className="w-[80px]"
                    data-autotest="legal-heatmap-table-pagination-page-size-select"
                    selectedKey={pageSize}
                    items={sizeItems}
                    onSelectionChange={(key: Key | null) => {
                        if (key === null) return;
                        const n = Number(key);
                        if (Number.isFinite(n)) onPageSizeChange(n);
                    }}
                >
                    {(item) => (
                        <Select.Item id={item.id} data-autotest={`legal-heatmap-table-pagination-page-size-${item.id}-option`}>
                            {item.label}
                        </Select.Item>
                    )}
                </Select>
            </div>
        </div>
    );
}

interface PageNavButtonProps {
    label: string;
    icon: React.FC<{ className?: string }>;
    disabled: boolean;
    onClick: () => void;
    "data-autotest"?: string;
}

function PageNavButton({ label, icon: Icon, disabled, onClick, "data-autotest": dataAutotest }: PageNavButtonProps) {
    return (
        <button
            type="button"
            aria-label={label}
            disabled={disabled}
            onClick={onClick}
            data-autotest={dataAutotest}
            className="text-tertiary hover:bg-secondary_hover hover:text-secondary size-8 flex items-center justify-center rounded-md transition duration-100 ease-linear disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
        >
            <Icon className="size-4" />
        </button>
    );
}
